// Study History Service
// Quản lý lịch sử học bài và tracking tiến độ học tập

import { sqliteDatabaseService } from '../database/SQLiteDatabaseService';
import { syncService } from '../sync/SyncService';
import {
  StudyProgress,
  CreateStudyProgressData,
  UpdateStudyProgressData,
  StudySession,
  CreateStudySessionData,
  UserStatistics,
  WeeklyStats,
  MonthlyStats,
  DayStats
} from '../../types/learningHistory';

class StudyHistoryService {
  private static instance: StudyHistoryService;
  
  private constructor() {}
  
  public static getInstance(): StudyHistoryService {
    if (!StudyHistoryService.instance) {
      StudyHistoryService.instance = new StudyHistoryService();
    }
    return StudyHistoryService.instance;
  }

  // ==================== STUDY PROGRESS ====================

  /**
   * Bắt đầu học một bài mới hoặc tiếp tục bài đã học
   */
  async startLesson(userId: string, lessonId: string, lessonTitle: string): Promise<StudyProgress> {
    console.log(`📖 Starting lesson: ${lessonId} for user: ${userId}`);

    // Check if lesson progress already exists
    const existing = await sqliteDatabaseService.getStudyProgress(userId, lessonId);
    
    if (existing) {
      console.log('✅ Lesson already started, updating lastAccessedAt');
      
      // Update lastAccessedAt
      await sqliteDatabaseService.updateStudyProgress(userId, lessonId, {
        lastAccessedAt: new Date().toISOString()
      });
      
      // Add to sync queue
      syncService.addToQueue({
        type: 'study_progress',
        action: 'update',
        data: { ...existing, lastAccessedAt: new Date().toISOString() }
      });
      
      return existing;
    }

    // Create new study progress
    const data: CreateStudyProgressData = {
      userId,
      lessonId,
      lessonTitle
    };

    const progress = await sqliteDatabaseService.createStudyProgress(data);
    
    // Add to sync queue
    syncService.addToQueue({
      type: 'study_progress',
      action: 'create',
      data: progress
    });
    
    console.log('✅ Lesson started successfully');
    
    return progress;
  }

  /**
   * Cập nhật tiến độ học bài
   */
  async updateLessonProgress(
    userId: string,
    lessonId: string,
    progress: number,
    timeSpent: number,
    interactiveStats?: UpdateStudyProgressData['interactiveStats'],
    progressBreakdown?: UpdateStudyProgressData['progressBreakdown']
  ): Promise<void> {
    console.log(`📝 Updating lesson progress: ${lessonId}, progress: ${progress}%, time: ${timeSpent}s`);

    const updates: UpdateStudyProgressData = {
      progress,
      timeSpent,
      lastAccessedAt: new Date().toISOString()
    };

    // Add interactiveStats if provided
    if (interactiveStats) {
      updates.interactiveStats = interactiveStats;
      console.log(`📊 Interactive stats: ${interactiveStats.correctAnswers}/${interactiveStats.totalQuestions} correct, score: ${interactiveStats.score}`);
    }

    // Add progressBreakdown if provided
    if (progressBreakdown) {
      updates.progressBreakdown = progressBreakdown;
      console.log(`📊 Progress breakdown - Interactive: ${progressBreakdown.interactive.progress.toFixed(1)}%, Videos: ${progressBreakdown.videos.progress.toFixed(1)}%, Quiz: ${progressBreakdown.quiz.progress.toFixed(1)}%, Simulations: ${progressBreakdown.simulations.progress.toFixed(1)}%`);
    }

    // If progress is 100%, mark as completed
    if (progress >= 100) {
      updates.status = 'completed';
      updates.completedAt = new Date().toISOString();
    } else if (progress > 0) {
      updates.status = 'in_progress';
    }

    await sqliteDatabaseService.updateStudyProgress(userId, lessonId, updates);
    
    // ✅ SYNC NGAY LẬP TỨC để user thấy thống kê real-time
    const updatedProgress = await sqliteDatabaseService.getStudyProgress(userId, lessonId);
    if (updatedProgress) {
      syncService.addToQueue({
        type: 'study_progress',
        action: 'update',
        data: updatedProgress
      });
      console.log('🔄 Added to sync queue immediately');
    }
    
    console.log('✅ Lesson progress updated');
  }

  /**
   * Đánh dấu bài học đã hoàn thành
   */
  async completeLesson(
    userId: string, 
    lessonId: string,
    interactiveStats?: UpdateStudyProgressData['interactiveStats']
  ): Promise<void> {
    console.log(`✅ Completing lesson: ${lessonId} for user: ${userId}`);

    const updates: UpdateStudyProgressData = {
      progress: 100,
      status: 'completed',
      completedAt: new Date().toISOString()
    };

    // Add interactiveStats if provided
    if (interactiveStats) {
      updates.interactiveStats = interactiveStats;
      console.log(`📊 Saving final stats: ${interactiveStats.correctAnswers}/${interactiveStats.totalQuestions} (${interactiveStats.score}%)`);
    }

    await sqliteDatabaseService.updateStudyProgress(userId, lessonId, updates);

    console.log('🎉 Lesson completed!');
  }

  /**
   * Lấy tiến độ học của 1 bài cụ thể
   */
  async getLessonProgress(userId: string, lessonId: string): Promise<StudyProgress | null> {
    return await sqliteDatabaseService.getStudyProgress(userId, lessonId);
  }

  /**
   * Lấy tất cả tiến độ học của user
   */
  async getAllLessonProgress(userId: string): Promise<StudyProgress[]> {
    return await sqliteDatabaseService.getAllStudyProgress(userId);
  }

  /**
   * Add bookmark to a lesson
   */
  async addBookmark(userId: string, lessonId: string, sectionId: string): Promise<void> {
    const progress = await this.getLessonProgress(userId, lessonId);
    
    if (!progress) {
      console.error('Cannot add bookmark: Lesson not started yet');
      return;
    }

    const bookmarks = progress.bookmarks || [];
    
    if (!bookmarks.includes(sectionId)) {
      bookmarks.push(sectionId);
      await sqliteDatabaseService.updateStudyProgress(userId, lessonId, {
        bookmarks
      });
      console.log('🔖 Bookmark added:', sectionId);
    }
  }

  /**
   * Remove bookmark
   */
  async removeBookmark(userId: string, lessonId: string, sectionId: string): Promise<void> {
    const progress = await this.getLessonProgress(userId, lessonId);
    
    if (!progress) return;

    const bookmarks = (progress.bookmarks || []).filter(id => id !== sectionId);
    
    await sqliteDatabaseService.updateStudyProgress(userId, lessonId, {
      bookmarks
    });
    
    console.log('🔖 Bookmark removed:', sectionId);
  }

  /**
   * Add/Update notes for a lesson
   */
  async updateNotes(userId: string, lessonId: string, notes: string): Promise<void> {
    await sqliteDatabaseService.updateStudyProgress(userId, lessonId, {
      notes
    });
    console.log('📝 Notes updated');
  }

  // ==================== STUDY SESSIONS ====================

  /**
   * Start a study session
   */
  async startSession(
    userId: string,
    sessionType: 'lesson' | 'quiz' | 'practice' | 'review',
    contentId: string
  ): Promise<string> {
    console.log(`🎯 Starting ${sessionType} session for content: ${contentId}`);

    const sessionData: CreateStudySessionData = {
      userId,
      sessionType,
      contentId,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        online: navigator.onLine
      }
    };

    const sessionId = await sqliteDatabaseService.startStudySession(sessionData);
    
    console.log('✅ Session started:', sessionId);
    return sessionId;
  }

  /**
   * End a study session
   */
  async endSession(sessionId: string, duration: number, actions?: any[]): Promise<void> {
    console.log(`🛑 Ending session: ${sessionId}, duration: ${duration}s`);

    await sqliteDatabaseService.endStudySession(sessionId, duration, actions);
    
    console.log('✅ Session ended');
  }

  /**
   * Get recent study sessions
   */
  async getRecentSessions(userId: string, limit: number = 10): Promise<StudySession[]> {
    return await sqliteDatabaseService.getRecentSessions(userId, limit);
  }

  // ==================== STATISTICS ====================

  /**
   * Get comprehensive user statistics
   */
  async getUserStatistics(userId: string): Promise<UserStatistics> {
    console.log(`📊 Calculating statistics for user: ${userId}`);

    // Get all study progress
    const allProgress = await sqliteDatabaseService.getAllStudyProgress(userId);
    
    // Get all quiz results
    const allQuizResults = await sqliteDatabaseService.getQuizResults(userId);
    
    // Get achievements
    const achievements = await sqliteDatabaseService.getUserAchievements(userId);

    // Calculate stats
    const totalLessons = allProgress.length;
    const completedLessons = allProgress.filter(p => p.status === 'completed').length;
    const inProgressLessons = allProgress.filter(p => p.status === 'in_progress').length;
    
    const totalQuizzes = allQuizResults.length;
    const totalScore = allQuizResults.reduce((sum, r) => sum + r.score, 0);
    const averageScore = totalQuizzes > 0 ? totalScore / totalQuizzes : 0;
    
    const totalStudyTime = allProgress.reduce((sum, p) => sum + (p.timeSpent || 0), 0);

    // Calculate streaks (simplified - you can enhance this)
    const currentStreak = await this.calculateCurrentStreak(userId);
    const longestStreak = await this.calculateLongestStreak(userId);

    // Recent activity
    const recentActivity = await this.getRecentActivity(userId, 10);

    // Quiz type distribution
    const quizzesByType = {
      TN: 0,
      DS: 0,
      TL: 0
    };
    
    allQuizResults.forEach(result => {
      result.answers.forEach(answer => {
        if (answer.questionType && quizzesByType[answer.questionType] !== undefined) {
          quizzesByType[answer.questionType]++;
        }
      });
    });

    // Score distribution
    const scoreDistribution = {
      excellent: allQuizResults.filter(r => r.score >= 90).length,
      good: allQuizResults.filter(r => r.score >= 70 && r.score < 90).length,
      fair: allQuizResults.filter(r => r.score >= 50 && r.score < 70).length,
      poor: allQuizResults.filter(r => r.score < 50).length
    };

    return {
      userId,
      totalLessons,
      completedLessons,
      inProgressLessons,
      totalQuizzes,
      averageScore,
      totalStudyTime,
      currentStreak,
      longestStreak,
      achievements,
      recentActivity,
      quizzesByType,
      scoreDistribution
    };
  }

  /**
   * Get weekly study time
   */
  async getWeeklyStudyTime(userId: string): Promise<WeeklyStats> {
    console.log(`📅 Calculating weekly stats for user: ${userId}`);

    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
    weekStart.setHours(0, 0, 0, 0);

    const allProgress = await sqliteDatabaseService.getAllStudyProgress(userId);
    const allQuizResults = await sqliteDatabaseService.getQuizResults(userId);

    // Group by day
    const days: DayStats[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + i);
      
      // Filter activities for this day
      const dayProgress = allProgress.filter(p => {
        const lastAccessed = new Date(p.lastAccessedAt);
        return lastAccessed.toDateString() === dayDate.toDateString();
      });

      const dayQuizzes = allQuizResults.filter(r => {
        const createdAt = new Date(r.createdAt);
        return createdAt.toDateString() === dayDate.toDateString();
      });

      const studyTime = dayProgress.reduce((sum, p) => sum + (p.timeSpent || 0), 0);
      const totalScore = dayQuizzes.reduce((sum, r) => sum + r.score, 0);
      const avgScore = dayQuizzes.length > 0 ? totalScore / dayQuizzes.length : 0;

      days.push({
        date: dayDate,
        dayOfWeek: dayNames[i],
        studyTime,
        lessonsCompleted: dayProgress.filter(p => p.status === 'completed').length,
        quizzesCompleted: dayQuizzes.length,
        averageScore: avgScore
      });
    }

    const totalStudyTime = days.reduce((sum, d) => sum + d.studyTime, 0);
    const totalLessons = days.reduce((sum, d) => sum + d.lessonsCompleted, 0);
    const totalQuizzes = days.reduce((sum, d) => sum + d.quizzesCompleted, 0);
    const totalScore = days.reduce((sum, d) => sum + d.averageScore * d.quizzesCompleted, 0);
    const averageScore = totalQuizzes > 0 ? totalScore / totalQuizzes : 0;

    return {
      week: `${now.getFullYear()}-W${this.getWeekNumber(now)}`,
      days,
      totalStudyTime,
      totalLessons,
      totalQuizzes,
      averageScore
    };
  }

  /**
   * Get monthly progress
   */
  async getMonthlyProgress(userId: string): Promise<MonthlyStats> {
    // Simplified version - you can enhance this
    const weeklyStats = await this.getWeeklyStudyTime(userId);
    
    return {
      month: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      totalStudyTime: weeklyStats.totalStudyTime,
      totalLessons: weeklyStats.totalLessons,
      totalQuizzes: weeklyStats.totalQuizzes,
      averageScore: weeklyStats.averageScore,
      weeklyBreakdown: [weeklyStats]
    };
  }

  // ==================== PRIVATE HELPERS ====================

  private async calculateCurrentStreak(userId: string): Promise<number> {
    // Simplified - count consecutive days with activity
    // You can enhance this with more sophisticated logic
    return 0; // Placeholder
  }

  private async calculateLongestStreak(userId: string): Promise<number> {
    // Simplified - track longest consecutive study days
    return 0; // Placeholder
  }

  private async getRecentActivity(userId: string, limit: number): Promise<any[]> {
    const progress = await sqliteDatabaseService.getAllStudyProgress(userId);
    const quizzes = await sqliteDatabaseService.getQuizResults(userId, { limit: limit / 2 });
    
    const activities: any[] = [];

    // Add lesson activities
    progress.slice(0, Math.floor(limit / 2)).forEach(p => {
      activities.push({
        id: p.id,
        type: 'lesson',
        title: p.lessonTitle,
        timestamp: new Date(p.lastAccessedAt),
        metadata: { progress: p.progress, status: p.status }
      });
    });

    // Add quiz activities
    quizzes.forEach(q => {
      activities.push({
        id: q.id,
        type: 'quiz',
        title: q.quizTitle,
        timestamp: new Date(q.createdAt),
        metadata: { score: q.score, passed: q.passed }
      });
    });

    // Sort by timestamp
    return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, limit);
  }

  private getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }
}

export const studyHistoryService = StudyHistoryService.getInstance();
export default StudyHistoryService;

